import { Game, PracticeShot } from '../types/curling';
import {
  QuadrantEndType,
  QuadrantSide,
  QuadrantDirection,
  QuadrantTimeEstimate,
  estimateQuadrantTime,
  sideFromBroomPosition,
  turnToDirection,
  endTypeFromEndNumber,
} from './teeLineEstimate';

export interface Zone8Entry {
  key: string;
  endType: QuadrantEndType;
  side: QuadrantSide;
  direction: QuadrantDirection;
  shots: number;
  avgCurl: number;
  // Last-N-shot curl, used instead of the game-long average while a game is
  // still in progress — an average is misleading for planning the next shot
  // when recent ends may have changed conditions.
  recentCurl: number | null;
  recentCurlShots: number;
  // Draw-to-tee-line time via the shared cascade in teeLineEstimate: this
  // quadrant's own shots, then its mirror's first shot, then a practice or
  // game-setup baseline.
  timeEstimate: QuadrantTimeEstimate;
}

const QUADRANTS: Array<{ endType: QuadrantEndType; side: QuadrantSide; direction: QuadrantDirection }> = [
  { endType: 'away', side: 'left', direction: 'left-right' },
  { endType: 'away', side: 'left', direction: 'right-left' },
  { endType: 'away', side: 'right', direction: 'left-right' },
  { endType: 'away', side: 'right', direction: 'right-left' },
  { endType: 'home', side: 'left', direction: 'left-right' },
  { endType: 'home', side: 'left', direction: 'right-left' },
  { endType: 'home', side: 'right', direction: 'left-right' },
  { endType: 'home', side: 'right', direction: 'right-left' },
];

const RECENT_SHOT_COUNT = 3;

const quadrantKey = (endType: QuadrantEndType, side: QuadrantSide, direction: QuadrantDirection) =>
  `${endType}-${side}-${direction}`;

/**
 * Per-quadrant (broom side x turn x end) curl and draw-to-tee-line time for
 * the Sheet Visualizer — shared by every place it's rendered (the Sheet Map
 * page, the live game summary, and the shot entry screen) so they all show
 * the same numbers.
 */
export const computeZone8Data = (game: Game, practiceShots: PracticeShot[]): Zone8Entry[] => {
  const isLive = !game.isComplete;
  const curlsByQuadrant = new Map<string, { value: number; timestamp: number }[]>();

  game.ends.forEach(end => {
    const endType = endTypeFromEndNumber(end.number);
    end.shots.forEach(shot => {
      if (shot.skipped) return;
      const key = quadrantKey(endType, sideFromBroomPosition(shot.broomPosition), turnToDirection(shot.turn));
      if (!curlsByQuadrant.has(key)) curlsByQuadrant.set(key, []);
      curlsByQuadrant.get(key)!.push({ value: shot.curl, timestamp: shot.timestamp });
    });
  });

  return QUADRANTS.map(({ endType, side, direction }) => {
    const curls = curlsByQuadrant.get(quadrantKey(endType, side, direction)) ?? [];
    const recentCurls = [...curls].sort((a, b) => a.timestamp - b.timestamp).slice(-RECENT_SHOT_COUNT);

    return {
      key: quadrantKey(endType, side, direction),
      endType,
      side,
      direction,
      shots: curls.length,
      avgCurl: curls.length > 0 ? curls.reduce((sum, c) => sum + c.value, 0) / curls.length : 0,
      recentCurl: recentCurls.length > 0
        ? recentCurls.reduce((sum, c) => sum + c.value, 0) / recentCurls.length
        : null,
      recentCurlShots: recentCurls.length,
      timeEstimate: estimateQuadrantTime(game, practiceShots, { endType, side, direction }, isLive),
    };
  });
};
